import { DialogPanel } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import React, { ReactElement } from 'react';
import { ShowcasePanel } from 'modules/ShowcasePanel';
import SignIn from 'modules/SignIn';
import { closeLoginModal } from 'xstate/actors/ModalActor';

// Split login dialog: the brand artwork on the left (lg+) and the sign-in
// options on the right — the /login page condensed into a modal so visitors
// can sign up without leaving the page they're on.
const LoginModal = (): ReactElement => {
  const t = useTranslations();
  return (
    <div className={styles.outer}>
      <div className={styles.inner}>
        <DialogPanel
          transition
          aria-label={t('auth.title.default')}
          className={styles.panel}
        >
          <ShowcasePanel variant="modal" className={styles.artwork} />
          <div className={styles.auth}>
            <SignIn />
          </div>
          <button
            type="button"
            onClick={closeLoginModal}
            aria-label="Close"
            className={styles.close}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </DialogPanel>
      </div>
    </div>
  );
};

export default LoginModal;

const styles = {
  outer: 'fixed inset-0 z-10 w-screen overflow-y-auto',
  inner: 'flex min-h-full items-center justify-center p-4 sm:p-8',
  panel: classNames(
    'relative grid w-full max-w-[480px] overflow-hidden rounded-[28px]',
    'shadow-[0_30px_90px_-24px_rgba(39,87,186,0.45)] ring-1 ring-mf-blue/15',
    'lg:h-[min(760px,calc(100dvh-4rem))] lg:max-w-[1240px]',
    'lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]',
    'transition-all data-closed:translate-y-4 data-closed:opacity-0',
    'data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in',
    'sm:data-closed:translate-y-0 sm:data-closed:scale-95',
  ),
  // Light theme: the page's white + dot grid; dark keeps the panel's navy.
  artwork: classNames(
    'bg-white bg-[radial-gradient(circle,rgba(39,87,186,0.08)_1px,transparent_1.4px)] bg-[length:28px_28px]',
    'marketing-dark:bg-none',
  ),
  // Same cool blue-white surface as the /login auth card.
  auth: classNames(
    'flex flex-col justify-center overflow-y-auto px-6 pb-10 pt-6 text-center sm:px-12',
    '[background:linear-gradient(180deg,#ffffff_0%,#eef2fc_100%)]',
  ),
  close: classNames(
    'absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full',
    'bg-white/80 text-mf-gray-600 shadow-sm ring-1 ring-mf-gray-200 backdrop-blur',
    'hover:bg-white hover:text-mf-off-black-950',
  ),
};
